/**
 * VMware sFlow network-traffic analysis — 1-1 parity with @console ManageNetworkTraffic.
 *
 * Charts (mirrors @console exactly, ported to @vnetwork/ui):
 *   1. Traffic timeline  — line, dual-axis (Kbps + packets), last 100 events
 *   2. Top sources       — horizontal bar (by packets)
 *   3. Port distribution — donut (top ports by MB)
 *   4. Protocol dist.    — pie  (TCP/UDP/ICMP/Other flow count)
 *   5. PPS distribution  — donut (packets-per-sec buckets)
 *   6. Bandwidth dist.   — donut (byte-size buckets)
 *
 * Controls:
 *   - Direction filter  (incoming / outgoing)
 *   - Flow limit        (flows fetched per request)
 *   - Source limit      (top-source-IP rows fetched per request)
 *
 * Tables (unchanged from original):
 *   - Top source IPs
 *   - Recent flows
 */

import { useMemo, useState } from 'react';
import { Activity } from 'lucide-react';
import { Chart, PieChart } from '@vnetwork/ui/charts';
import { Stat, DataTable, EmptyState, type ColumnDef } from '@vnetwork/ui/data';
import { Card, CardHeader, CardTitle, CardBody, Badge, Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@vnetwork/ui/primitives';
import { Section } from '@vnetwork/ui/layout';
import {
  useInstanceSflow,
  useInstanceSflowSources,
  type SFlowLog,
  type SFlowSource,
} from '@/api/vmware/instances';
import { formatBytes } from '@/pages/services/_shared/analytics-format';
import {
  buildTimeline,
  buildTopSources,
  buildPortDistribution,
  buildProtocolDistribution,
  buildPpsDistribution,
  buildBandwidthDistribution,
} from './components/sflow-chart-data';

// ---- Constants -----------------------------------------------------------

const PROTO: Record<number, string> = { 1: 'ICMP', 6: 'TCP', 17: 'UDP' };
const proto = (n?: number) => (n == null ? '—' : PROTO[n] ?? String(n));

const FLOW_LIMIT_OPTIONS = [50, 100, 250, 500] as const;
const SOURCE_LIMIT_OPTIONS = [10, 20, 50] as const;
type Direction = 'outgoing' | 'incoming';

// ---- Summary stat helpers ------------------------------------------------

function fmtMbps(mbps: number): string {
  if (mbps >= 1000) return `${(mbps / 1000).toFixed(2)} Gbps`;
  return `${mbps.toFixed(2)} Mbps`;
}

function fmtPackets(n: number): string {
  if (n >= 1_000_000_000) return `${(n / 1_000_000_000).toFixed(2)} B`;
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(2)} M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(2)} K`;
  return n.toLocaleString();
}

// ---- Table columns -------------------------------------------------------

const sourceCols: ColumnDef<SFlowSource, unknown>[] = [
  { id: 'ip',    header: 'Source IP',   cell: ({ row }) => <span style={{ fontWeight: 500 }}>{row.original.src_ip || '—'}</span> },
  { id: 'conn',  header: 'Connections', cell: ({ row }) => row.original.connection_count ?? 0 },
  { id: 'bytes', header: 'Bytes',       cell: ({ row }) => formatBytes(row.original.total_bytes) },
  { id: 'pkts',  header: 'Packets',     cell: ({ row }) => (row.original.total_packets ?? 0).toLocaleString() },
  { id: 'proto', header: 'Protocols',   cell: ({ row }) => (row.original.protocols ?? []).map(proto).join(', ') || '—' },
];

const flowCols: ColumnDef<SFlowLog, unknown>[] = [
  { id: 'src',   header: 'Source',      cell: ({ row }) => `${row.original.src_ip ?? '—'}:${row.original.src_port ?? '—'}` },
  { id: 'dst',   header: 'Destination', cell: ({ row }) => `${row.original.dst_ip ?? '—'}:${row.original.dst_port ?? '—'}` },
  { id: 'proto', header: 'Protocol',    cell: ({ row }) => proto(row.original.protocol) },
  { id: 'bytes', header: 'Bytes',       cell: ({ row }) => formatBytes(row.original.bytes) },
  { id: 'pps',   header: 'pps',         cell: ({ row }) => (row.original.pps ?? 0).toLocaleString() },
];

// ---- Component -----------------------------------------------------------

export function NetworkTrafficTab({ serviceId, instanceId }: { serviceId: string; instanceId: string }) {
  const [direction, setDirection] = useState<Direction>('outgoing');
  const [flowLimit, setFlowLimit] = useState<number>(50);
  const [sourceLimit, setSourceLimit] = useState<number>(20);

  const { data: sourcesData, isLoading: sLoading, isError: sErr } =
    useInstanceSflowSources(serviceId, instanceId, { limit: sourceLimit });

  const { data: flowsData, isLoading: fLoading, isError: fErr } =
    useInstanceSflow(serviceId, instanceId, { limit: flowLimit, direction });

  const summary = sourcesData?.summary ?? flowsData?.summary ?? {};
  const sources = sourcesData?.sources ?? [];
  const logs    = flowsData?.logs ?? [];
  const loading = sLoading || fLoading;
  const unavailable = (sErr && fErr) || (!loading && sources.length === 0 && logs.length === 0);

  // ---- Chart data (memoised) -------------------------------------------
  const timeline            = useMemo(() => buildTimeline(logs), [logs]);
  const topSourcesBar       = useMemo(() => buildTopSources(sources, 10), [sources]);
  const portDist            = useMemo(() => buildPortDistribution(logs), [logs]);
  const protoDist           = useMemo(() => buildProtocolDistribution(logs), [logs]);
  const ppsDist             = useMemo(() => buildPpsDistribution(logs), [logs]);
  const bwDist              = useMemo(() => buildBandwidthDistribution(logs), [logs]);

  // ---- Controls UI --------------------------------------------------------
  const controls = (
    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)', flexWrap: 'wrap' }}>
      {/* Direction */}
      <Select value={direction} onValueChange={(v) => setDirection(v as Direction)}>
        <SelectTrigger size="sm" style={{ minWidth: 120 }}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="outgoing">Outgoing</SelectItem>
          <SelectItem value="incoming">Incoming</SelectItem>
        </SelectContent>
      </Select>

      {/* Flow limit */}
      <Select value={String(flowLimit)} onValueChange={(v) => setFlowLimit(Number(v))}>
        <SelectTrigger size="sm" style={{ minWidth: 110 }}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {FLOW_LIMIT_OPTIONS.map((n) => (
            <SelectItem key={n} value={String(n)}>{n} flows</SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* Source limit */}
      <Select value={String(sourceLimit)} onValueChange={(v) => setSourceLimit(Number(v))}>
        <SelectTrigger size="sm" style={{ minWidth: 120 }}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {SOURCE_LIMIT_OPTIONS.map((n) => (
            <SelectItem key={n} value={String(n)}>{n} sources</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );

  return (
    <Section
      title={
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--vn-space-2)' }}>
          Network traffic
          <Badge tone="info" appearance="subtle" size="sm">Beta</Badge>
        </span>
      }
      description="sFlow-based bandwidth, top source IPs, distribution charts and recent flows."
      actions={controls}
    >
      {unavailable ? (
        <EmptyState
          icon={<Activity size={28} />}
          title="No traffic data"
          description="sFlow traffic analysis isn't enabled for this instance's zone, or there's no recent traffic to report."
        />
      ) : (
        <div style={{ display: 'grid', gap: 'var(--vn-space-5)' }}>

          {/* ---- Summary stats row ---------------------------------------- */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 'var(--vn-space-3)' }}>
            <Card flat><CardBody><Stat label="Bandwidth"          value={fmtMbps(summary.total_bandwidth_mbps ?? 0)} /></CardBody></Card>
            <Card flat><CardBody><Stat label="Total traffic"      value={formatBytes(summary.total_bytes)} /></CardBody></Card>
            <Card flat><CardBody><Stat label="Packets"            value={fmtPackets(summary.total_packets ?? 0)} /></CardBody></Card>
            <Card flat><CardBody><Stat label="Unique sources"     value={(summary.unique_sources ?? sourcesData?.total ?? 0).toLocaleString()} /></CardBody></Card>
            <Card flat><CardBody><Stat label="Unique destinations" value={(summary.unique_destinations ?? 0).toLocaleString()} /></CardBody></Card>
          </div>

          {/* ---- Chart 1: Traffic timeline (full width) ------------------- */}
          {timeline.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Traffic timeline <span style={{ color: 'var(--vn-fg-muted)', fontWeight: 400 }}>(last {timeline.length} events)</span></CardTitle>
              </CardHeader>
              <CardBody>
                {/* Dual-axis: primary = Kbps, secondary = packets (separate series colours) */}
                <Chart
                  type="area"
                  data={timeline}
                  xKey="t"
                  series={[
                    { key: 'kbps',    label: 'Bandwidth (Kbps)' },
                    { key: 'packets', label: 'Packets' },
                  ]}
                  height={220}
                  showLegend
                  showGrid
                />
              </CardBody>
            </Card>
          )}

          {/* ---- Chart 2: Top source IPs bar (full width) ----------------- */}
          {topSourcesBar.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Top source IPs <span style={{ color: 'var(--vn-fg-muted)', fontWeight: 400 }}>(by packets)</span></CardTitle>
              </CardHeader>
              <CardBody>
                <Chart
                  type="bar"
                  data={topSourcesBar}
                  xKey="ip"
                  series={[{ key: 'packets', label: 'Packets' }]}
                  height={220}
                  showGrid
                  valueFormatter={(v) => {
                    const n = Number(v);
                    if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
                    if (n >= 1_000)     return `${(n / 1_000).toFixed(0)}K`;
                    return String(n);
                  }}
                />
              </CardBody>
            </Card>
          )}

          {/* ---- Charts 3-6: 2-column distribution pies ------------------- */}
          {logs.length > 0 && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(320px, 100%), 1fr))', gap: 'var(--vn-space-4)' }}>

              {/* Chart 3: Port distribution */}
              {portDist.length > 0 && (
                <Card>
                  <CardHeader><CardTitle>Port distribution <span style={{ color: 'var(--vn-fg-muted)', fontWeight: 400 }}>(MB)</span></CardTitle></CardHeader>
                  <CardBody>
                    <PieChart
                      data={portDist}
                      donut
                      height={200}
                      showLegend
                      valueFormatter={(v) => `${Number(v).toFixed(2)} MB`}
                    />
                  </CardBody>
                </Card>
              )}

              {/* Chart 4: Protocol distribution */}
              {protoDist.length > 0 && (
                <Card>
                  <CardHeader><CardTitle>Protocol distribution <span style={{ color: 'var(--vn-fg-muted)', fontWeight: 400 }}>(flows)</span></CardTitle></CardHeader>
                  <CardBody>
                    <PieChart
                      data={protoDist}
                      height={200}
                      showLegend
                      valueFormatter={(v) => `${Number(v).toLocaleString()} flows`}
                    />
                  </CardBody>
                </Card>
              )}

              {/* Chart 5: PPS distribution */}
              {ppsDist.length > 0 && (
                <Card>
                  <CardHeader><CardTitle>PPS distribution <span style={{ color: 'var(--vn-fg-muted)', fontWeight: 400 }}>(events)</span></CardTitle></CardHeader>
                  <CardBody>
                    <PieChart
                      data={ppsDist}
                      donut
                      height={200}
                      showLegend
                      valueFormatter={(v) => `${Number(v).toLocaleString()} events`}
                    />
                  </CardBody>
                </Card>
              )}

              {/* Chart 6: Bandwidth distribution */}
              {bwDist.length > 0 && (
                <Card>
                  <CardHeader><CardTitle>Bandwidth distribution <span style={{ color: 'var(--vn-fg-muted)', fontWeight: 400 }}>(events)</span></CardTitle></CardHeader>
                  <CardBody>
                    <PieChart
                      data={bwDist}
                      donut
                      height={200}
                      showLegend
                      valueFormatter={(v) => `${Number(v).toLocaleString()} events`}
                    />
                  </CardBody>
                </Card>
              )}
            </div>
          )}

          {/* ---- Tables (unchanged from original) ------------------------- */}
          <Section title="Top source IPs">
            <DataTable
              columns={sourceCols}
              data={sources}
              getRowId={(r, i) => r.src_ip ?? String(i)}
              emptyState={<p style={{ margin: 0, color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)' }}>No source data.</p>}
            />
          </Section>

          <Section title="Recent flows">
            <DataTable
              columns={flowCols}
              data={logs}
              getRowId={(_r, i) => String(i)}
              emptyState={<p style={{ margin: 0, color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)' }}>No recent flows.</p>}
            />
          </Section>

        </div>
      )}
    </Section>
  );
}
